/**
 * KI-Kennzeichnung — AI-generated image badge.
 *
 * Two jobs:
 *  1. Open the shared #ai-badge-modal info dialog when a badge is activated
 *     (capture phase so the click never zooms the gallery or follows a card link),
 *     and return focus to the badge on close.
 *  2. Inject badges onto image surfaces that snippets/ai-badge.liquid does NOT
 *     server-render — CSS background-image sections (bgset / inline style) and raw
 *     <img> tags — so that *every* image whose file name contains "-kigen" is
 *     labelled, including AJAX-loaded and future content.
 *
 * SSR badges (product gallery, product/collection cards, content images through
 * responsive-image / media) come from Liquid and carry class .rimage__image on
 * their <img>; this scanner deliberately skips those to avoid double-badging.
 *
 * See docs/adr/0003-ai-image-labeling.md and CONTEXT.md ("KI-Kennzeichnung").
 */
(function () {
  'use strict';

  var MARKER = '-kigen';

  // Chrome / UI surfaces that reuse product imagery but must not be badged
  // (logos, payment/social icons, swatches, cart & mini-cart thumbnails, the
  // added-to-cart popup, gallery thumbnails, nav CTAs and video posters).
  var EXCLUDE = [
    '.product-area__thumbs', '.theme-gallery-thumb',
    '.cc-swatches', '[data-option-item]',
    'cart-drawer', '.theme-modal--drawer', '.cart-product', // AJAX drawer is a <cart-drawer> element
    '#added-to-cart', '.theme-modal--small',
    '.logo', '.footer',
    '.payment-icons', '.social-icons',
    '.nav-ctas', '.nav-call-to-action',
    '[data-video-type]', '.product-media--video', '.background-video', '.video-container'
  ].join(',');

  function hasMarker(str) {
    return !!str && str.toLowerCase().indexOf(MARKER) !== -1;
  }

  function excluded(el) {
    return !!(el && el.closest && el.closest(EXCLUDE));
  }

  function makeBadge() {
    var tpl = document.getElementById('ai-badge-template');
    if (!tpl || !tpl.content) return null;
    var node = tpl.content.firstElementChild;
    return node ? node.cloneNode(true) : null;
  }

  var IMG_SELECTOR = 'img:not(.rimage__image):not(.icon):not(.zoom-image)';
  var BG_SELECTOR = '[data-bgset],[style*="background-image"]';

  // Try to place a badge in `container`; returns true on success. Skips if the
  // container is missing, excluded chrome, or already holds a badge.
  function tryInject(container) {
    if (!container || excluded(container)) return false;
    if (container.querySelector('.ai-badge')) return false;
    var badge = makeBadge();
    if (!badge) return false;
    // Anchor the absolutely-positioned badge to this element.
    if (window.getComputedStyle(container).position === 'static') {
      container.classList.add('ai-badge-host');
    }
    container.appendChild(badge);
    return true;
  }

  // Badge one source image exactly once. Prefer `preferred` (the overlay owner,
  // so the badge clears the tint/text on hero sections); if that is missing or
  // already used by ANOTHER image, fall back to the image's own wrapper — so
  // every tagged image keeps its own badge and images never collapse onto one
  // shared container. The source is marked so rescans never double-badge it.
  function badgeSource(source, preferred, fallback) {
    if (source.hasAttribute('data-ai-badged')) return;
    if (tryInject(preferred) || tryInject(fallback)) {
      source.setAttribute('data-ai-badged', '');
    }
  }

  // On hero sections the image wrapper is often transformed (its own stacking
  // context) with a full-area text overlay beside it — a badge inside the wrapper
  // is painted under the overlay/tint and cannot be clicked. Anchor instead to
  // the nearest POSITIONED ancestor that owns the text overlay (the .slide /
  // gallery item, skipping a static wrapping <a>). Keyed on the specific
  // .overlay-type class — NOT the generic .overlay — so an unrelated overlay
  // elsewhere on the page can't hijack the anchor and collapse several images.
  function overlayOwner(el) {
    var node = el.parentElement;
    for (var i = 0; node && i < 6; i++) {
      if (
        node.querySelector('.overlay-type') &&
        window.getComputedStyle(node).position !== 'static'
      ) {
        return node;
      }
      node = node.parentElement;
    }
    return null;
  }

  function markBackground(el) {
    if (!hasMarker(el.getAttribute('data-bgset')) && !hasMarker(el.getAttribute('style'))) return;
    badgeSource(el, overlayOwner(el), el);
  }

  function imgHasMarker(img) {
    return (
      hasMarker(img.getAttribute('src')) ||
      hasMarker(img.getAttribute('data-src')) ||
      hasMarker(img.getAttribute('srcset')) ||
      hasMarker(img.getAttribute('data-srcset')) ||
      hasMarker(img.getAttribute('data-bgset')) ||
      hasMarker(img.currentSrc)
    );
  }

  // Raw <img> not produced by the SSR snippets (those carry .rimage__image),
  // not decorative icons, not the mobile zoom stack. (<noscript> images are not
  // in the live DOM when JS is on.)
  function markImage(img) {
    if (!imgHasMarker(img) || excluded(img)) return;
    var wrapper =
      img.closest('.rimage-outer-wrapper,.rimage-wrapper,.image__first,.image-inner,.image') ||
      img.parentElement;
    badgeSource(img, wrapper, img.parentElement);
  }

  // Scan a subtree (an added node from the observer, or the whole document on
  // first run), testing the root itself as well as its descendants.
  function scanRoot(root) {
    if (!document.getElementById('ai-badge-template')) return; // feature off
    if (root.nodeType === 1) {
      if (root.matches(BG_SELECTOR)) markBackground(root);
      if (root.matches(IMG_SELECTOR)) markImage(root);
    }
    if (!root.querySelectorAll) return;
    var bg = root.querySelectorAll(BG_SELECTOR);
    for (var i = 0; i < bg.length; i++) markBackground(bg[i]);
    var imgs = root.querySelectorAll(IMG_SELECTOR);
    for (var j = 0; j < imgs.length; j++) markImage(imgs[j]);
  }

  // Coalesce rescans of only the newly-added subtrees (quick-buy, cart, filtered
  // collections, infinite scroll). Our own appendChild also mutates the DOM, but
  // the "already badged" guard makes the follow-up scan a no-op, so it converges.
  var queued = [];
  var pending = false;
  function flush() {
    pending = false;
    var roots = queued;
    queued = [];
    for (var i = 0; i < roots.length; i++) scanRoot(roots[i]);
  }
  function scheduleScan(root) {
    queued.push(root);
    if (pending) return;
    pending = true;
    if (window.requestAnimationFrame) { window.requestAnimationFrame(flush); }
    else { window.setTimeout(flush, 60); }
  }

  function initScanner() {
    scanRoot(document);
    if (!window.MutationObserver || !document.body) return;
    new window.MutationObserver(function (mutations) {
      for (var i = 0; i < mutations.length; i++) {
        var added = mutations[i].addedNodes;
        for (var j = 0; j < added.length; j++) {
          if (added[j].nodeType === 1) scheduleScan(added[j]);
        }
      }
    }).observe(document.body, { childList: true, subtree: true });
  }

  // ---- Dialog open + focus management ----------------------------------------
  var lastBadge = null;

  function openModal(badge) {
    var modal = document.getElementById('ai-badge-modal');
    if (!modal) return;
    lastBadge = badge || null;
    if (window.showInPageModal && window.jQuery) {
      window.showInPageModal(window.jQuery(modal));
    } else {
      modal.classList.add('reveal');
      document.body.classList.add('modal-active');
    }
  }

  function badgeFrom(target) {
    return target && target.closest ? target.closest('.ai-badge') : null;
  }

  document.addEventListener('click', function (e) {
    var badge = badgeFrom(e.target);
    if (!badge) return;
    e.preventDefault();
    e.stopPropagation();
    openModal(badge);
  }, true);

  document.addEventListener('keydown', function (e) {
    if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
    var badge = badgeFrom(e.target);
    if (!badge) return;
    e.preventDefault();
    e.stopPropagation();
    openModal(badge);
  }, true);

  // The theme fires ccModalClosed on every modal close; lastBadge is only set
  // when a badge opened this dialog, so unrelated modals no-op.
  if (window.jQuery) {
    window.jQuery(window).on('ccModalClosed', function () {
      if (lastBadge && typeof lastBadge.focus === 'function') lastBadge.focus();
      lastBadge = null;
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initScanner);
  } else {
    initScanner();
  }
})();
